import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import { collection, addDoc, getDocs, query, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Button, Paper, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import "./styles/Companies.scss";

const Companies = () => {
    const { currentUser, currentService } = useContext(AuthContext);
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newCompany, setNewCompany] = useState({
        nombre: '',
        cif: '',
        codEmpresa: '',
        logo: null
    });
    const [editCompany, setEditCompany] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [editPreviewUrl, setEditPreviewUrl] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentService?.id) {
            return;
        }
        fetchCompanies();
    }, [currentService]);

    const fetchCompanies = async () => {
        if (!currentService?.id) {
            console.error("No hay servicio seleccionado");
            return;
        }

        try {
            const companiesRef = collection(db, 'servicios', currentService.id, 'empresas');
            const companiesSnap = await getDocs(query(companiesRef));
            const companiesData = companiesSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCompanies(companiesData);
        } catch (error) {
            console.error("Error al cargar empresas:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCompany(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditCompany(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewCompany(prev => ({
                ...prev,
                logo: file
            }));

            // Crear preview del logo
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEditFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditCompany(prev => ({
                ...prev,
                logo: file,
                isLogoUpdated: true
            }));

            // Crear preview del logo
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateCodEmpresa = (code) => {
        // Verificar que sea un número de 4 dígitos
        const regex = /^\d{4}$/;
        return regex.test(code);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validar código de empresa
        if (!validateCodEmpresa(newCompany.codEmpresa)) {
            alert('El código de empresa debe ser un número de 4 dígitos');
            return;
        }
        
        setIsLoading(true);

        try {
            let logoUrl = '';
            if (newCompany.logo) {
                // Subir logo al Storage
                const logoRef = ref(storage, `servicios/${currentService.id}/empresas/logos/${newCompany.logo.name}`);
                await uploadBytes(logoRef, newCompany.logo);
                logoUrl = await getDownloadURL(logoRef);
            }

            // Añadir empresa a Firestore
            const companyData = {
                nombre: newCompany.nombre,
                cif: newCompany.cif,
                codEmpresa: newCompany.codEmpresa,
                logoUrl: logoUrl,
                createdAt: new Date()
            };

            const companiesRef = collection(db, 'servicios', currentService.id, 'empresas');
            await addDoc(companiesRef, companyData);

            // Limpiar formulario y recargar empresas
            setNewCompany({
                nombre: '',
                cif: '',
                codEmpresa: '',
                logo: null
            });
            setPreviewUrl(null);
            await fetchCompanies();

            alert('Empresa añadida correctamente');
        } catch (error) {
            console.error("Error al añadir empresa:", error);
            alert('Error al añadir la empresa');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenEditDialog = (company) => {
        setEditCompany({
            ...company,
            logo: null,
            isLogoUpdated: false
        });
        setEditPreviewUrl(company.logoUrl);
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setEditCompany(null);
        setEditPreviewUrl(null);
    };

    const handleUpdateCompany = async () => {
        if (!validateCodEmpresa(editCompany.codEmpresa)) {
            alert('El código de empresa debe ser un número de 4 dígitos');
            return;
        }

        setIsLoading(true);

        try {
            let logoUrl = editCompany.logoUrl;
            
            // Solo subir nuevo logo si se ha actualizado
            if (editCompany.logo && editCompany.isLogoUpdated) {
                const logoRef = ref(storage, `servicios/${currentService.id}/empresas/logos/${editCompany.logo.name}`);
                await uploadBytes(logoRef, editCompany.logo);
                logoUrl = await getDownloadURL(logoRef);
            }

            // Actualizar empresa en Firestore
            const companyRef = doc(db, 'servicios', currentService.id, 'empresas', editCompany.id);
            await updateDoc(companyRef, {
                nombre: editCompany.nombre,
                cif: editCompany.cif,
                codEmpresa: editCompany.codEmpresa,
                logoUrl: logoUrl,
                updatedAt: new Date()
            });

            await fetchCompanies();
            handleCloseEditDialog();
            alert('Empresa actualizada correctamente');
        } catch (error) {
            console.error("Error al actualizar empresa:", error);
            alert('Error al actualizar la empresa');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="companies-container">
            <Typography variant="h4" component="h1" className="companies-title">
                Gestión de Empresas
            </Typography>

            {/* Formulario para añadir empresa */}
            <Paper elevation={3} className="company-form-paper">
                <Typography variant="h6">Añadir Nueva Empresa</Typography>
                <form onSubmit={handleSubmit} className="company-form">
                    <TextField
                        name="nombre"
                        label="Nombre de la Empresa"
                        value={newCompany.nombre}
                        onChange={handleInputChange}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        name="cif"
                        label="CIF"
                        value={newCompany.cif}
                        onChange={handleInputChange}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        name="codEmpresa"
                        label="Código de Empresa (4 dígitos)"
                        value={newCompany.codEmpresa}
                        onChange={handleInputChange}
                        required
                        fullWidth
                        margin="normal"
                        inputProps={{ 
                            maxLength: 4,
                            pattern: "[0-9]{4}"
                        }}
                        helperText="Debe ser un número de 4 dígitos"
                    />
                    <div className="logo-upload">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            id="logo-input"
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="logo-input">
                            <Button variant="contained" component="span">
                                Subir Logo
                            </Button>
                        </label>
                        {previewUrl && (
                            <div className="logo-preview">
                                <img src={previewUrl} alt="Logo preview" />
                            </div>
                        )}
                    </div>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        className="submit-button"
                    >
                        {isLoading ? 'Añadiendo...' : 'Añadir Empresa'}
                    </Button>
                </form>
            </Paper>

            {/* Lista de empresas */}
            <div className="companies-list">
                {isLoading ? (
                    <p>Cargando empresas...</p>
                ) : (
                    companies.map((company) => (
                        <Paper key={company.id} elevation={3} className="company-paper">
                            <div className="company-info">
                                {company.logoUrl && (
                                    <div className="company-logo">
                                        <img src={company.logoUrl} alt={`Logo de ${company.nombre}`} />
                                    </div>
                                )}
                                <div className="company-details">
                                    <Typography variant="h6">{company.nombre}</Typography>
                                    <Typography variant="body1">CIF: {company.cif}</Typography>
                                    <Typography variant="body1">Código: {company.codEmpresa || 'No asignado'}</Typography>
                                </div>
                                <div className="company-actions">
                                    <IconButton 
                                        color="primary" 
                                        onClick={() => handleOpenEditDialog(company)}
                                        className="edit-button"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </Paper>
                    ))
                )}
            </div>

            {/* Diálogo para editar empresa */}
            <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Empresa</DialogTitle>
                <DialogContent>
                    {editCompany && (
                        <div className="edit-company-form">
                            <TextField
                                name="nombre"
                                label="Nombre de la Empresa"
                                value={editCompany.nombre}
                                onChange={handleEditInputChange}
                                required
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                name="cif"
                                label="CIF"
                                value={editCompany.cif}
                                onChange={handleEditInputChange}
                                required
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                name="codEmpresa"
                                label="Código de Empresa (4 dígitos)"
                                value={editCompany.codEmpresa}
                                onChange={handleEditInputChange}
                                required
                                fullWidth
                                margin="normal"
                                inputProps={{ 
                                    maxLength: 4,
                                    pattern: "[0-9]{4}"
                                }}
                                helperText="Debe ser un número de 4 dígitos"
                            />
                            <div className="logo-upload">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleEditFileChange}
                                    id="edit-logo-input"
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="edit-logo-input">
                                    <Button variant="contained" component="span">
                                        Actualizar Logo
                                    </Button>
                                </label>
                                {editPreviewUrl && (
                                    <div className="logo-preview">
                                        <img src={editPreviewUrl} alt="Logo preview" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} color="primary">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleUpdateCompany} 
                        color="primary" 
                        variant="contained"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Actualizando...' : 'Guardar Cambios'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Companies;